import { Choices } from './choice';
import { ContentId } from './content-block';
import { FunctionalElement, FUNCTIONAL_ELEMENT_TYPE } from './functional-element';

export type OptionDependency = Readonly<{
    option: ContentId; // the id of the OPTION element
    functionalElements: ContentId[]; // the ids of all dependent functional elements
    checkpoints: ContentId[]; // the ids of all dependent checkpoints
}>;

// the key of this object is the content id of the option functional element
export type OptionDependencies = Readonly<{
    [key: number]: undefined | OptionDependency;
}>;

// OPTION has an inverse logic for the TYPE of the selected radio button compared to checkpoints. Here RADIOBTN.VALUE2
// means YES (in a checkpoint, VALUE1 would mean YES)
export enum OPTION_FUNCTIONAL_ELEMENT_TYPE {
    YES = 'RADIOBTN.VALUE2',
    NO = 'RADIOBTN.VALUE1',
}

export type Option = AstoriaOption | CosimaOption;

export type BaseOption = Readonly<{
    xmlId: string;
    label: string;
    yesLabel: string;
    noLabel: string;
    // OPTION never has a controlling checkpoint, since it functions as a controlling element itself
    controllingCheckpoint: '';
}> &
    FunctionalElement;

export const initialBaseOptionState: BaseOption = {
    id: -1,
    xmlId: '',
    label: '',
    yesLabel: '',
    noLabel: '',
    contentLoadedFromXml: false,
    controllingCheckpoint: '',
    controllingOption: undefined,
    disabled: false,
    parentId: 1,
    type: FUNCTIONAL_ELEMENT_TYPE.OPTION,
};

export type AstoriaOption = Readonly<{
    selected: OPTION_FUNCTIONAL_ELEMENT_TYPE.YES | OPTION_FUNCTIONAL_ELEMENT_TYPE.NO | undefined;
    type: FUNCTIONAL_ELEMENT_TYPE.OPTION;
}> &
    BaseOption;

export const initialAstoriaOptionState: AstoriaOption = {
    ...initialBaseOptionState,
    selected: undefined,
    type: FUNCTIONAL_ELEMENT_TYPE.OPTION,
};

export type CosimaOption = Readonly<{
    choices: Choices;
    type: FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT;
    visible: boolean;
}> &
    BaseOption;

export const initialCosimaOptionState: CosimaOption = {
    ...initialBaseOptionState,
    choices: {},
    type: FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT,
    visible: false,
};

export const initialOptionDependency: OptionDependency = {
    option: -1,
    functionalElements: [],
    checkpoints: [],
};
export const initialOptionDependencies: OptionDependencies = {};

export function isCosimaOption(f: FunctionalElement): f is CosimaOption {
    return f.type === FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT;
}

export function isAstoriaOption(f: FunctionalElement): f is AstoriaOption {
    return f.type === FUNCTIONAL_ELEMENT_TYPE.OPTION;
}
